import { useState, type ReactElement } from 'react';
import { useHistory } from 'react-router-dom';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useJobs, type Job } from '../../contexts/JobsContext';
import { checkExportStatus } from '../../utils/exportService';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';

const ENTITY_TYPE_LABELS: Record<string, string> = {
  transactions: 'Payments',
  contacts: 'Contacts',
  subscriptions: 'Subscriptions',
  payouts: 'Payouts',
  'payouts-drilldown': 'Payout Details',
  'payouts-report': 'Payout Report',
  'tax-documents': 'Tax Documents',
  'email-tax-documents': 'Email Tax Receipts',
  forms: 'Forms',
  elements: 'Elements',
  'fundraiser-drilldown': 'Fundraiser Details',
  'event-drilldown': 'Event Details',
};

const ACTION_TYPE_LABELS: Record<string, string> = {
  MARK_TAX_DEDUCTIBLE: 'Mark Tax Deductible',
  RESEND_RECEIPTS: 'Resend Receipts',
  SOFT_DELETE: 'Delete Payments',
  CANCEL_SUBSCRIPTIONS: 'Cancel Subscriptions',
  ACTIVATE_SUBSCRIPTIONS: 'Activate Subscriptions',
  ACTIVATE_FORMS: 'Activate Forms',
  DEACTIVATE_FORMS: 'Deactivate Forms',
  ACTIVATE_ELEMENTS: 'Activate Elements',
  DEACTIVATE_ELEMENTS: 'Deactivate Elements',
  DELETE_URL_ELEMENTS: 'Delete URL Elements',
};

function formatTimeAgo(timestamp: number): string {
  const seconds = Math.floor(Date.now() / 1000 - timestamp);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatDate(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function getJobLabel(job: Job): string {
  if (job.type === 'notification') {
    return job.actionLabel || 'Action';
  }
  if (job.type === 'export') {
    const entityLabel = ENTITY_TYPE_LABELS[job.entityType || ''] || job.entityType || 'Records';
    return `${entityLabel} Export`;
  }
  if (job.type === 'import') {
    return 'Contact Import';
  }
  return ACTION_TYPE_LABELS[job.actionType || ''] || job.actionType || 'Bulk Action';
}

function getProgressPercent(job: Job): number | null {
  // A REVERSING import keeps the counts from its original SUCCESS — reversal
  // outcomes live in a separate `reversal` map the panel does not read (§3.1).
  // Charting the stale counts would peg the bar at 100% for the whole reversal
  // and then "finish", which reads as progress running backwards. No bar is
  // better than a wrong one.
  if (job.type === 'import' && job.importStatus === 'REVERSING') {
    return null;
  }
  if (job.totalCount && job.totalCount > 0 && job.processedCount !== undefined) {
    return Math.round((job.processedCount / job.totalCount) * 100);
  }
  return null;
}

function StatusIcon({ status }: { status: Job['status'] }) {
  if (status === 'PENDING' || status === 'PROCESSING') {
    return (
      <span className="relative flex h-5 w-5 shrink-0 items-center justify-center">
        <span className="absolute inline-flex h-2 w-2 animate-ping rounded-full bg-sage opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-sage" />
      </span>
    );
  }
  if (status === 'COMPLETED') {
    return (
      <svg
        className="h-5 w-5 shrink-0 text-sage"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
        />
      </svg>
    );
  }
  if (status === 'FAILED') {
    return (
      <svg
        className="h-5 w-5 shrink-0 text-red-400"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
        />
      </svg>
    );
  }
  return null;
}

function getStatusDetail(job: Job): string | null {
  const isActive = job.status === 'PENDING' || job.status === 'PROCESSING';

  if (isActive && job.type === 'export') {
    const action =
      job.status === 'PENDING'
        ? 'Preparing...'
        : job.entityType === 'email-tax-documents'
          ? 'Sending tax receipts...'
          : job.entityType === 'tax-documents'
            ? 'Generating tax documents...'
            : 'Generating CSV...';
    const count =
      job.processedCount !== undefined && job.totalCount
        ? ` ${job.processedCount.toLocaleString()} / ${job.totalCount.toLocaleString()}`
        : (job.recordCount ?? 0) > 0
          ? ` (${job.recordCount?.toLocaleString()} records)`
          : '';
    return `${action}${count}`;
  }

  if (isActive && job.type === 'bulk-action') {
    return `Processing ${(job.processedCount || 0).toLocaleString()}${
      job.totalCount ? ` / ${job.totalCount.toLocaleString()}` : ''
    }`;
  }

  // Contact imports (PE-1911). The file name is what the user recognises — they
  // picked it minutes ago — so it leads the line where we have it.
  if (job.type === 'import') {
    const named = job.fileName ? ` ${job.fileName}` : '';

    if (isActive) {
      // Mid-COMMITTING the buckets are still climbing (§3.1), so a raw
      // processed/total is the honest read. Before the first batch manifest
      // lands there are no counts at all — say what is happening rather than
      // rendering "0 / 0".
      const counted =
        job.processedCount !== undefined && job.totalCount
          ? ` — ${job.processedCount.toLocaleString()} / ${job.totalCount.toLocaleString()} rows`
          : '';
      // REVERSING is a different verb from COMMITTING and the user asked for it
      // explicitly; conflating the two reads as the import re-running.
      const verb = job.importStatus === 'REVERSING' ? 'Reversing' : 'Importing';
      return `${verb}${named}${counted}`;
    }

    if (job.status === 'PENDING') {
      // A pre-commit draft (§3.2) — assembled but never run. It has imported
      // nothing, so it must not claim a count.
      return `Draft import${named} — not yet started`;
    }

    if (job.status === 'COMPLETED') {
      // CANCELLED and REVERSED are settled without being successes. §3.3.2
      // rolls their writes back, so `successCount` is legitimately 0 — and
      // "0 contacts imported" under a checkmark reads as a broken import
      // rather than as the undo the user asked for.
      if (job.importStatus === 'CANCELLED') {
        return `Import cancelled${named}. No contacts were changed`;
      }
      if (job.importStatus === 'REVERSED') {
        return `Import reversed${named}`;
      }
      if (job.importStatus === 'CLEANED_UP') {
        return 'Import resolved by support';
      }

      const landed = job.successCount ?? 0;
      const errored = job.failedCount ?? 0;
      const errors = errored > 0 ? `, ${errored.toLocaleString()} with errors` : '';
      return `${landed.toLocaleString()} contact${landed === 1 ? '' : 's'} imported${errors}`;
    }

    // FAILED covers FAILED and FAILED_NEEDS_CLEANUP. §3.3.2 rolls the writes
    // back, so quoting counts here would read as "0 contacts imported" — a
    // claim about the outcome rather than the cleanup.
    return job.importStatus === 'FAILED_NEEDS_CLEANUP'
      ? 'Import failed — some contacts may remain. Contact support'
      : 'Import failed. No contacts were changed';
  }

  if (job.status === 'COMPLETED' && job.type === 'export') {
    if (job.entityType === 'email-tax-documents') {
      const count = (job.recordCount || 0).toLocaleString();
      const suffix = (job.recordCount || 0) !== 1 ? 's' : '';
      const failed =
        (job.failedCount || 0) > 0 ? `, ${job.failedCount?.toLocaleString()} failed` : '';
      return `${count} tax receipt${suffix} sent${failed}`;
    }
    if (job.entityType === 'tax-documents') {
      // CON-857: no-donations contacts are skipped, not failed; each suffix only when > 0. Only a
      // job the CON-857 worker wrote (numeric skippedCount) splits the two, so an older job, whose
      // failedCount still counts skipped contacts, keeps today's line exactly.
      const splitsSkipped = typeof job.skippedCount === 'number';
      const skipped =
        (job.skippedCount || 0) > 0 ? `, ${job.skippedCount?.toLocaleString()} skipped` : '';
      const failed =
        splitsSkipped && (job.failedCount || 0) > 0
          ? `, ${job.failedCount?.toLocaleString()} failed`
          : '';
      return `${(job.recordCount || 0).toLocaleString()} tax documents generated${skipped}${failed}`;
    }
    return `${(job.recordCount || 0).toLocaleString()} records exported`;
  }

  if (job.status === 'COMPLETED' && job.type === 'bulk-action') {
    const succeeded = `${(job.successCount || 0).toLocaleString()} succeeded`;
    const failed =
      (job.failedCount || 0) > 0 ? `, ${job.failedCount?.toLocaleString()} failed` : '';
    return `${succeeded}${failed}`;
  }

  return null;
}

function JobCard({ job, onMarkRead }: { job: Job; onMarkRead?: (jobId: string) => void }) {
  const isActive = job.status === 'PENDING' || job.status === 'PROCESSING';
  const isCompleted = job.status === 'COMPLETED';
  const isFailed = job.status === 'FAILED';
  const isUnread = job.type === 'notification' && !job.isRead;
  const isExportNotification =
    job.type === 'notification' &&
    (job.actionType === 'EXPORT' || job.actionType === 'TAX_DOCUMENTS') &&
    !!job.originalJobId;
  const label = getJobLabel(job);
  const progress = getProgressPercent(job);
  const statusDetail = getStatusDetail(job);
  const [downloading, setDownloading] = useState(false);
  const history = useHistory();
  const { closePanel } = useJobs();

  // A notification can carry an in-app deep link (metadata.navigationUrl,
  // e.g. a contact-health alert → the affected contacts). Such cards stay
  // clickable even once read so the alert remains a way back to the records.
  const hasNavigation = typeof job.navigationUrl === 'string' && job.navigationUrl.startsWith('/');
  const isClickable = isUnread || hasNavigation;

  const handleClick = () => {
    if (isUnread && onMarkRead) {
      onMarkRead(job.jobId);
    }
    if (hasNavigation) {
      closePanel();
      history.push(job.navigationUrl as string);
    }
  };

  const showDownload = isCompleted && job.type === 'export' && job.downloadUrl;
  const showExportDownload = isCompleted && isExportNotification;

  return (
    <div
      className={`group rounded-md border border-edge bg-paper-inset p-4 transition-colors ${
        isClickable ? 'cursor-pointer hover:bg-edge/60' : ''
      }`}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClick();
              }
            }
          : undefined
      }
    >
      {/* Main row */}
      <div className="flex items-center justify-between gap-3">
        {/* Left: icon + text */}
        <div className="flex min-w-0 flex-1 items-start gap-2">
          <div className="pt-0.5">
            <StatusIcon status={job.status} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm text-ink">{label}</span>
              {/* Semantic variants (same as the transactions StatusBadge) for the
                  text/border status tokens, but on a light chip surface: the
                  tinted status backgrounds are designed for white surfaces and
                  are near-identical in luminance to this card's bg-paper-inset,
                  so they read as a muddy gray here. bg-paper-light lifts the
                  chip off the inset bg (like the Download button beside it)
                  and stays correct in dark mode, where bg-white would glare. */}
              {isFailed && (
                <Badge
                  variant="danger"
                  className="h-6 rounded-md bg-paper-light text-xs font-medium"
                >
                  Failed
                </Badge>
              )}
              {isActive && (
                <Badge
                  variant="success"
                  className="h-6 rounded-md bg-paper-light text-xs font-medium"
                >
                  In progress
                </Badge>
              )}
            </div>
            <p className="text-xs text-ink-muted">
              {statusDetail && (
                <>
                  <span className="text-ink-muted">{statusDetail}</span>
                  <span className="mx-1">·</span>
                </>
              )}
              {formatDate(job.createdAt)}
              <span className="mx-1">·</span>
              {formatTimeAgo(job.createdAt)}
            </p>
          </div>
        </div>

        {/* Right: action */}
        {showDownload && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 shrink-0 border-edge bg-white px-2.5 text-xs font-medium text-ink-secondary hover:bg-paper-muted"
            onClick={(e) => {
              e.stopPropagation();
              window.open(job.downloadUrl, '_blank');
            }}
          >
            Download
          </Button>
        )}
        {showExportDownload && (
          <Button
            variant="outline"
            size="sm"
            disabled={downloading}
            className="h-7 shrink-0 border-edge bg-white px-2.5 text-xs font-medium text-ink-secondary hover:bg-paper-muted"
            onClick={async (e) => {
              e.stopPropagation();
              setDownloading(true);
              try {
                const status = await checkExportStatus(job.originalJobId!);
                if (status.downloadUrl) {
                  window.open(status.downloadUrl, '_blank');
                } else {
                  toast.error('Download not available yet. Please try again shortly.');
                }
              } catch {
                toast.error('Failed to prepare download. Please try again.');
              } finally {
                setDownloading(false);
              }
            }}
          >
            {downloading ? 'Preparing...' : 'Download'}
          </Button>
        )}
      </div>

      {/* Error message */}
      {isFailed && job.error && (
        <p className="mt-2 text-xs text-red-500 line-clamp-2">{job.error}</p>
      )}

      {/* Progress bar */}
      {isActive && progress !== null && (
        <div className="mt-2">
          <div className="h-1 w-full overflow-hidden rounded-full bg-edge">
            <div
              className="h-full rounded-full bg-sage transition-all duration-300"
              style={{ width: `${Math.max(progress, 2)}%` }}
            />
          </div>
          <p className="mt-0.5 text-right text-[10px] text-ink-muted">{progress}%</p>
        </div>
      )}

      {/* Indeterminate progress */}
      {isActive && progress === null && (
        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-edge">
          <div className="h-full w-1/3 animate-pulse rounded-full bg-sage" />
        </div>
      )}
    </div>
  );
}

type TabFilter = 'unread' | 'read';

type JobsPanelProps = {
  children: ReactElement;
};

export function JobsPanel({ children }: JobsPanelProps) {
  const {
    jobs,
    isPanelOpen,
    openPanel,
    closePanel,
    markRead,
    markAllRead,
    unreadCount,
    isRefreshing,
    isInitializing,
  } = useJobs();
  const [activeTab, setActiveTab] = useState<TabFilter>('unread');

  const activeJobs = jobs.filter((j) => j.status === 'PENDING' || j.status === 'PROCESSING');
  const completedJobs = jobs.filter((j) => j.status === 'COMPLETED' || j.status === 'FAILED');

  // Filter completed jobs by tab. Locally tracked jobs have no read state, so
  // they remain in Unread as a temporary completion card until JobsContext
  // observes and swaps in the matching durable notification. This closes the
  // fast-job gap where Activity used to say "No unread notifications" between
  // those two events.
  const filteredJobs = completedJobs.filter((job) => {
    if (job.type !== 'notification') return activeTab === 'unread';
    if (activeTab === 'unread') return !job.isRead;
    return job.isRead === true;
  });

  const readCount = completedJobs.filter(
    (j) => j.type === 'notification' && j.isRead === true,
  ).length;

  const handleMarkRead = (jobId: string) => {
    markRead(jobId).catch(() => {
      // already logged in JobsContext; swallow to avoid unhandled rejection
    });
  };

  const tabs: { key: TabFilter; label: string; count?: number }[] = [
    { key: 'unread', label: 'Unread', count: unreadCount },
    { key: 'read', label: 'Read', count: readCount },
  ];

  return (
    <Popover
      open={isPanelOpen}
      onOpenChange={(open) => {
        if (open) {
          openPanel();
        } else {
          closePanel();
        }
      }}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>{children}</PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          aria-label="Notifications"
          data-label="Notifications"
          className="before:content-[attr(data-label)]"
        />
      </Tooltip>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={8}
        collisionPadding={12}
        className="flex w-[min(25rem,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-lg border-edge bg-white p-0 shadow-lg"
        style={{
          maxHeight: 'min(34rem, var(--radix-popover-content-available-height))',
        }}
      >
        <div className="shrink-0 border-b border-edge bg-white">
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-base font-semibold text-ink">Notifications</h2>
            <button
              onClick={closePanel}
              className="rounded-md p-1 text-ink-muted hover:bg-paper-inset hover:text-ink-secondary"
              aria-label="Close notifications"
            >
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex items-center justify-between px-4 pb-3">
            <div className="rounded-md border border-edge bg-paper-inset">
              <nav className="flex p-0.5" aria-label="Tabs">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`whitespace-nowrap rounded px-2.5 py-0.5 text-xs font-medium focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 ${
                      activeTab === tab.key
                        ? 'border border-edge bg-white text-ink'
                        : 'border border-transparent bg-transparent text-ink-muted hover:text-ink-secondary'
                    }`}
                  >
                    {tab.label}
                    {tab.count !== undefined && tab.count > 0 && (
                      <span
                        className={`ml-1 ${
                          activeTab === tab.key ? 'text-ink-muted' : 'text-ink-muted'
                        }`}
                      >
                        {tab.count}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>
            {unreadCount > 0 && (
              <button
                onClick={() => {
                  markAllRead().catch(() => {
                    // already logged in JobsContext; swallow to avoid unhandled rejection
                  });
                }}
                className="ml-3 shrink-0 text-xs font-medium text-sage hover:text-sage-hover"
              >
                Mark all read
              </button>
            )}
          </div>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          {/* Skeleton while loading initial notifications */}
          {isInitializing && jobs.length === 0 && (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="animate-pulse rounded-md border border-edge bg-paper-inset p-4"
                >
                  <div className="flex items-center gap-2">
                    <div className="h-5 w-5 rounded-full bg-edge" />
                    <div className="h-4 w-32 rounded bg-edge" />
                  </div>
                  <div className="mt-1.5 h-3 w-40 rounded bg-edge" />
                </div>
              ))}
            </div>
          )}

          {!isInitializing && jobs.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="mb-3 h-10 w-10 text-edge"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"
                />
              </svg>
              <p className="text-sm font-medium text-ink-muted">No notifications yet</p>
              <p className="mt-1 text-xs text-ink-muted">
                Exports, bulk actions, and notifications will appear here
              </p>
            </div>
          )}

          {/* Active jobs */}
          {activeJobs.length > 0 && (
            <div>
              <div className="space-y-2">
                {activeJobs.map((job) => (
                  <JobCard key={job.jobId} job={job} onMarkRead={handleMarkRead} />
                ))}
              </div>
            </div>
          )}

          {/* Skeleton while refreshing after job completes */}
          {isRefreshing && (
            <div>
              <div className="animate-pulse rounded-md border border-edge bg-paper-inset p-4">
                <div className="flex items-center gap-2">
                  <div className="h-5 w-5 rounded-full bg-edge" />
                  <div className="h-4 w-32 rounded bg-edge" />
                </div>
                <div className="mt-1.5 h-3 w-40 rounded bg-edge" />
              </div>
            </div>
          )}

          {/* Filtered completed/recent jobs */}
          {filteredJobs.length > 0 && (
            <div>
              <div className="space-y-2">
                {filteredJobs.map((job) => (
                  <JobCard key={job.jobId} job={job} onMarkRead={handleMarkRead} />
                ))}
              </div>
            </div>
          )}

          {jobs.length > 0 && activeJobs.length === 0 && filteredJobs.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-sm font-medium text-ink-muted">
                {activeTab === 'unread' ? 'No unread notifications' : 'No read notifications'}
              </p>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
